import {
  ICreatePassportDto,
  IEquipment,
  IEquipmentLocation,
  IEquipmentPassport,
  TCreateEquipmentDTO,
  TCreateEquipmentMaintenanceBlankDTO,
  TCreateEquipmentMaintenanceValueDTO,
  TEquipmentEventType,
} from './equipment.types.ts'
import { IMaintenanceBlankSchema } from '../Processes/Maintenance/maintenance.types.ts'
import domUtils from '@/utils/dom.utils.ts'
import config from '@/config.ts'
import mdmUtils from '../MDM/mdm.utils.ts'
import { IPassportSchema } from '../MDM/mdm.types.ts'

interface EquipmentAddressOptions {
  includeCountry?: boolean
  includeLocationDescription?: boolean
  includeCity?: boolean
  includeRegion?: boolean
  includeAddress?: boolean
}

const getEquipmentAddress = (
  location: IEquipmentLocation,
  {
    includeCountry = false,
    includeLocationDescription = true,
    includeCity = true,
    includeRegion = false,
    includeAddress = true,
  }: EquipmentAddressOptions = {},
  sep = ', '
) => {
  if (!location) return ''

  const parts = [
    includeCountry && location.country,
    includeRegion && location.region,
    includeCity && location.city,
    includeAddress && location.address,
    includeLocationDescription && location.locationDescription,
  ]

  return parts.filter(Boolean).join(sep)
}

const isEquipmentAddressEqual = (l1: IEquipmentLocation, l2: IEquipmentLocation) => {
  if (!l1 || !l2) return false
  if (l1.country !== l2.country) return false
  if (l1.region !== l2.region) return false
  if (l1.city !== l2.city) return false
  if (l1.address !== l2.address) return false
  return true
}

const getEquipmentEventTypeName = (eventType: TEquipmentEventType) => {
  switch (eventType) {
    case 'CONNECT':
      return 'Подключение'
    case 'DISCONNECT':
      return 'Отключение'
  }
}

const generateEmptyMaintenanceBlank = (equipmentId: IEquipment['id']): TCreateEquipmentMaintenanceBlankDTO => ({
  equipmentId,
  type: 'PLANNED',
  comment: '',
  blankSchemaId: null,
  values: [],
})

const generateEmptyUnplannedMaintenanceBlank = (
  equipmentId: IEquipment['id']
): TCreateEquipmentMaintenanceBlankDTO => ({
  equipmentId,
  type: 'UNPLANNED',
  comment: '',
  values: [],
})

const generateEmptyEquipment = (): TCreateEquipmentDTO => ({
  name: '',
  modelId: null,
  connectionConfiguration: {},
  protocolId: null,
  organizations: [],
  description: '',
  responsibleUsers: [],
  location: {
    longitude: null,
    latitude: null,
    address: '',
    locationDescription: '',
    country: '',
    region: '',
    city: '',
    postalCode: null,
  },
})

const equipmentToDto = (equipment: IEquipment): TCreateEquipmentDTO => ({
  name: equipment.name,
  connectionConfiguration: equipment.connectionConfiguration,
  protocolId: equipment.protocolId,
  modelId: equipment.modelId,
  organizations: equipment.organizations.map((o) => o.organization.id),
  description: equipment.description,
  responsibleUsers: equipment.responsibleUsers.map((u) => u.user.id),

  location: {
    longitude: equipment.location.longitude,
    latitude: equipment.location.latitude,
    address: equipment.location.address,
    locationDescription: equipment.location.locationDescription,
    country: equipment.location.country,
    region: equipment.location.region,
    city: equipment.location.city,
    postalCode: equipment.location.postalCode,
  },
})

const generateEmptyMaintenanceBlankValues = (
  schema: IMaintenanceBlankSchema
): Array<TCreateEquipmentMaintenanceValueDTO> => {
  if (!schema) return []
  return schema.fields.map((f) => ({
    blankSchemaFieldId: f.id,
    value: '',
    enumValueId: null,
    unitValueId: null,
  }))
}

const getStateText = (state: IEquipment['state']) => {
  switch (state) {
    case 'PENDING':
      return 'На обслуживании'
    case 'ERROR':
      return 'Авария'
    case 'STOPPED':
      return 'Не подключен'
    case 'RUNNING':
      return 'Активен'
    case 'DISCONNECTED':
      return 'Отключен'
  }
}

const getStatusFlagColor = (flag: IEquipment['statusFlag']) => {
  switch (flag) {
    case 'ERROR':
    case 'FATAL_FAULT':
      return '#F5222D'
    case 'WARNING':
      return '#FAAD14'
    case 'IN_MAINTENANCE':
      return '#1677FF'
    case 'NORMAL':
      return '#52C41A'
  }
}

const passportInstanceToPassportDTO = (passport: IEquipmentPassport): ICreatePassportDto => {
  if (!passport) return null

  return {
    passportSchemaId: passport.passportId,
    values: passport.values.map((v) => ({
      enumValueId: v.enumValueId,
      value: v.value,
      unitValueId: v.unitValueId,
      fieldId: v.fieldId,
    })),
  }
}

const openPDFPassport = (equipmentId: IEquipment['id'], accessToken: string) => {
  domUtils.openLink(
    config.API_URL + `/docs/v1/passports/generate?equipmentId=${equipmentId}&accessToken=${accessToken}`
  )
}

const fillMissingPassportValues = (passport: ICreatePassportDto, passportSchema: IPassportSchema) => {
  if (!passport || !passportSchema) return
  const passportValueFieldIdSet = new Set(passport.values.map((v) => v.fieldId))

  for (let field of passportSchema.fields) {
    if (!passportValueFieldIdSet.has(field.id)) {
      const passportValueDto = mdmUtils.generateCreatePassportValueDTO(field.id)
      passport['values'].push(passportValueDto)
    }
  }
  return passport
}

const equipmentUtils = {
  isEquipmentAddressEqual,
  getEquipmentEventTypeName,
  generateEmptyMaintenanceBlank,
  generateEmptyMaintenanceBlankValues,
  getStateText,
  generateEmptyEquipment,
  passportInstanceToPassportDTO,
  equipmentToDto,
  getEquipmentAddress,
  fillMissingPassportValues,
  openPDFPassport,
  generateEmptyUnplannedMaintenanceBlank,
  getStatusFlagColor,
}

export default equipmentUtils
